import React from "react";
import ReactDOM from "react-dom";

export type MenuItem = {
    id: string;
    label: string;
    icon?: React.ReactNode;
    disabled?: boolean;
    separator?: boolean;
};

type Props = {
    x: number;
    y: number;
    items: MenuItem[];
    onSelect: (id: string) => void;
    onClose: () => void;
    darkMode?: boolean;
};

type State = {
    selectedIndex: number;
};

export default class ContextMenu extends React.Component<Props, State> {
    private containerRef = React.createRef<HTMLDivElement>();
    private portalEl: HTMLDivElement;

    state: State = {
        selectedIndex: -1
    };

    constructor(props: Props) {
        super(props);
        this.portalEl = document.createElement("div");
        this.portalEl.style.position = "fixed";
        this.portalEl.style.top = "0";
        this.portalEl.style.left = "0";
        this.portalEl.style.zIndex = "10100";
    }

    componentDidMount() {
        document.body.appendChild(this.portalEl);
        document.addEventListener("keydown", this.handleKeyDown);
        // Focus the menu so it can receive blur events
        requestAnimationFrame(() => {
            this.containerRef.current?.focus();
        });
    }

    componentWillUnmount() {
        document.body.removeChild(this.portalEl);
        document.removeEventListener("keydown", this.handleKeyDown);
    }

    handleBlur = (e: React.FocusEvent) => {
        // Check if focus moved outside the menu
        if (!this.containerRef.current?.contains(e.relatedTarget as Node)) {
            this.props.onClose();
        }
    };

    handleKeyDown = (e: KeyboardEvent) => {
        const enabledItems = this.props.items.filter(i => !i.disabled && !i.separator);
        switch (e.key) {
            case "Escape":
                e.preventDefault();
                this.props.onClose();
                break;
            case "ArrowDown":
                e.preventDefault();
                this.setState(s => ({
                    selectedIndex: Math.min(s.selectedIndex + 1, enabledItems.length - 1)
                }));
                break;
            case "ArrowUp":
                e.preventDefault();
                this.setState(s => ({
                    selectedIndex: Math.max(s.selectedIndex - 1, 0)
                }));
                break;
            case "Enter":
                e.preventDefault();
                if (this.state.selectedIndex >= 0 && this.state.selectedIndex < enabledItems.length) {
                    this.props.onSelect(enabledItems[this.state.selectedIndex].id);
                }
                break;
        }
    };

    render() {
        const { x, y, items, darkMode = true } = this.props;
        const { selectedIndex } = this.state;

        const menuStyle: React.CSSProperties = {
            position: "fixed",
            left: x,
            top: y,
            minWidth: 180,
            background: darkMode ? "rgba(24, 26, 32, 0.98)" : "rgba(255, 255, 255, 0.98)",
            borderRadius: 8,
            boxShadow: darkMode
                ? "0 4px 20px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)"
                : "0 4px 20px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.08)",
            padding: "4px 0",
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: 13,
            outline: "none"
        };

        let enabledIndex = -1;

        return ReactDOM.createPortal(
            <div
                ref={this.containerRef}
                style={menuStyle}
                tabIndex={-1}
                onBlur={this.handleBlur}
                onMouseLeave={this.props.onClose}
            >
                {items.map((item, i) => {
                    if (item.separator) {
                        return <div key={i} style={{
                            height: 1,
                            background: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
                            margin: "4px 8px"
                        }} />;
                    }

                    if (!item.disabled) enabledIndex++;
                    const isSelected = enabledIndex === selectedIndex;

                    return (
                        <div
                            key={item.id}
                            style={{
                                padding: "8px 12px",
                                cursor: item.disabled ? "default" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                color: item.disabled
                                    ? (darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)")
                                    : (darkMode ? "#e0e0e0" : "#333"),
                                background: isSelected
                                    ? (darkMode ? "rgba(100,255,180,0.15)" : "rgba(0,0,0,0.05)")
                                    : "transparent"
                            }}
                            onClick={() => !item.disabled && this.props.onSelect(item.id)}
                            onMouseEnter={() => {
                                if (!item.disabled) {
                                    // Find the enabled index for this item
                                    const enabledItems = this.props.items.filter(i => !i.disabled && !i.separator);
                                    const idx = enabledItems.findIndex(ei => ei.id === item.id);
                                    if (idx >= 0) {
                                        this.setState({ selectedIndex: idx });
                                    }
                                }
                            }}
                        >
                            {item.icon && <span style={{ width: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>{item.icon}</span>}
                            <span>{item.label}</span>
                        </div>
                    );
                })}
            </div>,
            this.portalEl
        );
    }
}

export { ContextMenu };
